import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { VaultErrorSection } from '../session/VaultErrorSection';
import { usePlan } from '../plan/usePlan';
import { useCatalog } from '../entity/catalogStore';
import { hiddenStateOf } from '../entity/visibility';
import { EMPTY_FOLDERS, rootListing } from '../entity/folders';
import { contentsOf, countMembers, coveredNotes } from './membership';
import { buildCorpus } from '../thoughts/corpus';
import { buildGathering } from '../thoughts/gathering';
import { useThoughtWindow } from '../thoughts/useThoughtWindow';
import { useColumnCount } from '../thoughts/ThoughtWall';
import { cardFill } from '../design/tokens';
import { useTheme } from '../design/useTheme';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import { isArchived } from '../plan/arc';
import { FolderPage, Inside } from './FolderPage';
import { folderMark, markWhereDiffers, tagMarkOf } from './TagMark';
import type { TagMark } from './TagMark';
import { parseDistilled, renderPathFor } from './distilled';
import type { Distilled } from './distilled';
import { readDeeper, writeDeeper } from './folderView';
import { Loading } from '../shell/Loading';
import './folders.css';
import '../thoughts/thoughts.css';

/**
 * Folders, as somewhere to go.
 *
 * The third surface, and a deliberate divergence from the phone, which rules a
 * folders tab OUT at four tabs. The web is where the pile gets worked, and
 * until a folder is openable, moving a live subject into one means losing
 * sight of it — which is the reason the folder conversion kept stalling.
 *
 * A folder is walked by its breadcrumb and the tiles of the folders inside
 * it; the root is the tiles of every notebook ([rootListing]).
 */

/** Notes rendered before the page asks for more. Reads follow rendering, so
 *  this is also how much decryption a folder costs to open. */
const PAGE = 60;

export function FoldersRoute() {
  const { folderId } = useParams();
  const { state, reload } = useVault();
  const { theme } = useTheme();
  const { plan: planState } = usePlan();
  const catalog = useCatalog();
  const root = useRef<HTMLDivElement>(null);
  const columns = useColumnCount(root);

  const plan = planState.status === 'ready' ? planState.plan : null;
  const registries = plan?.registries ?? EMPTY_REGISTRIES;
  const folders = registries.folders ?? EMPTY_FOLDERS;
  const arcs = useMemo(() => plan?.arcs ?? [], [plan]);

  const vault = state.status === 'ready' ? state.vault : null;
  const corpus = useMemo(() => (vault ? buildCorpus(vault) : []), [vault]);

  // Keyed on the folder, so walking from one that was read deep into another
  // does not open the second one's first two hundred files at once.
  const [limit, setLimit] = useState(PAGE);
  const [limitFor, setLimitFor] = useState(folderId);
  const [include, setInclude] = useState(() => (folderId ? readDeeper(folderId) : false));
  if (limitFor !== folderId) {
    setLimitFor(folderId);
    setLimit(PAGE);
    setInclude(folderId ? readDeeper(folderId) : false);
  }

  const nowMs = useMemo(() => Date.now(), [corpus]);
  // The same gate the thoughts feed applies, for the same reason: a folder
  // filled with what the app has finished with is not what it holds.
  const visibleCorpus = useMemo(
    () => corpus.filter((item) => !hiddenStateOf(catalog.get(item.path), nowMs)),
    [corpus, catalog, nowMs],
  );
  // The same gate on the other kind of member: a folder that took a retired
  // storyline's members should not also list the storyline as live work.
  const visibleArcs = useMemo(() => arcs.filter((arc) => !isArchived(arc)), [arcs]);

  /** What every folder holds, for the tiles that walk into them. */
  const counts = useMemo(
    () => countMembers(folders, visibleArcs, visibleCorpus, catalog),
    [folders, visibleArcs, visibleCorpus, catalog],
  );

  const here = folderId ? folders.byId.get(folderId) : undefined;
  const missing = folderId !== undefined && here === undefined && planState.status === 'ready';
  const parent = here?.parentId ? (folders.byId.get(here.parentId) ?? null) : null;

  const held = useMemo(
    () => (here ? contentsOf(here, visibleArcs, visibleCorpus, catalog) : { arcs: [], notes: [] }),
    [here, visibleArcs, visibleCorpus, catalog],
  );
  const notes = useMemo(
    () => (here && include ? coveredNotes(here, visibleCorpus, catalog) : held.notes),
    [here, include, visibleCorpus, catalog, held],
  );

  // What is on screen decides what is read.
  const wanted = useMemo(() => notes.slice(0, limit).map((n) => n.path), [notes, limit]);
  const opened = useThoughtWindow(vault, wanted);
  const gathering = useMemo(() => buildGathering(arcs), [arcs]);

  /** A card wears its thought's first tag, as on the Thoughts page. */
  const fillOf = (path: string): string => {
    const tag = catalog.get(path)?.tags[0];
    return cardFill(theme, tag ? tagTint(registries, tag) : null);
  };
  /** The folder's own mark is said by the page; a card marks only one that
   *  looks different. */
  const markOf = (path: string): TagMark | null =>
    markWhereDiffers(tagMarkOf(catalog.get(path)?.tags ?? [], folders, theme), here ? folderMark(here, theme) : null);

  const distilled = useDistilled(here?.kind === 'vertical' ? here.name : null);

  const chooseInclude = useCallback(
    (on: boolean) => {
      if (here) writeDeeper(here.id, on);
      setInclude(on);
    },
    [here],
  );

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  if (state.status === 'error') return <VaultErrorSection state={state} reload={reload} />;

  if (planState.status === 'loading' || planState.status === 'idle') {
    return <Loading title="Reading the tags…" />;
  }

  if (folders.all.length === 0) {
    return (
      <section className="empty">
        <h1>No tags in this vault.</h1>
        <p className="t-bodyProse">
          Make a tag on the phone, or through the assistant,
          and it will be here.
        </p>
      </section>
    );
  }

  if (missing) {
    return (
      <section className="placeholder">
        <h1>No tag with that id.</h1>
        <p className="t-bodyProse">
          It may have been renamed away or never reached this browser. <Link to="/folders">All tags</Link>.
        </p>
      </section>
    );
  }

  if (!here) {
    return (
      <div className="fd-page" ref={root}>
        <Inside folders={rootListing(folders)} counts={counts} columns={columns} theme={theme} />
      </div>
    );
  }

  return (
    <FolderPage
      key={here.id}
      folder={here}
      parent={parent}
      arcs={held.arcs}
      notes={notes}
      include={include}
      onInclude={chooseInclude}
      distilled={distilled}
      opened={opened}
      gathering={gathering}
      fillOf={fillOf}
      markOf={markOf}
      counts={counts}
      theme={theme}
      limit={limit}
      onLimit={setLimit}
    />
  );
}

/** A vertical's distilled state, read off its render where there is one. */
function useDistilled(name: string | null): Distilled | null {
  const { state } = useVault();
  const [distilled, setDistilled] = useState<Distilled | null>(null);
  useEffect(() => {
    setDistilled(null);
    const path = name ? renderPathFor(name) : null;
    if (!path || state.status !== 'ready' || !state.vault.listPaths('verticals/').includes(path)) return;
    let cancelled = false;
    void state.vault.readFiles([path]).then(([read]) => {
      if (!cancelled && read?.text) setDistilled(parseDistilled(read.text));
    });
    return () => {
      cancelled = true;
    };
  }, [name, state]);
  return distilled;
}
